import facts from './productFacts.json';

/**
 * Product facts shared by every page that states them (prices, discount,
 * money-back period, contact). The values live in productFacts.json so the
 * Node build scripts (llms.txt) read the same file; this module adds the
 * formatted strings the copy uses. src/seo/productFacts.test.ts fails when
 * any copy states an amount that is not derived from these facts.
 */
export const PRODUCT_FACTS = facts;

/** Groups thousands without relying on the runtime's locale data. */
const group = (amount: number, separator: string): string =>
  String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

/** "15.000 ₺" (Turkish) or "₺15,000" (English). */
export function formatTry(amount: number, lang: 'tr' | 'eng'): string {
  return lang === 'tr' ? `${group(amount, '.')} ₺` : `₺${group(amount, ',')}`;
}

/** Discount against the list price, rounded to a whole percent (25, 38). */
const discountPercent = (price: number, listPrice: number): number =>
  Math.round((1 - price / listPrice) * 100);

const { monthly, annual } = facts.plans;

/** Every price-related figure the copy shows, formatted per language. */
const priceText = (lang: 'tr' | 'eng') => ({
  monthly: formatTry(monthly.price, lang),
  monthlyList: formatTry(monthly.listPrice, lang),
  monthlySaving: formatTry(monthly.listPrice - monthly.price, lang),
  monthlyDiscount: discountPercent(monthly.price, monthly.listPrice),
  annual: formatTry(annual.price, lang),
  annualList: formatTry(annual.listPrice, lang),
  annualSaving: formatTry(annual.listPrice - annual.price, lang),
  annualDiscount: discountPercent(annual.price, annual.listPrice),
  maxDiscount: Math.max(
    discountPercent(monthly.price, monthly.listPrice),
    discountPercent(annual.price, annual.listPrice)
  ),
  moneyBackDays: facts.moneyBackDays,
});

export const PRICE_TEXT = { tr: priceText('tr'), eng: priceText('eng') };

/** Every TRY amount the copy may state: prices, list prices and savings. */
export const ALLOWED_AMOUNTS: readonly number[] = [monthly, annual].flatMap((plan) => [
  plan.price,
  plan.listPrice,
  plan.listPrice - plan.price,
]);
